import React, { useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from '../components/RichTextEditor';
import TermsAccordion from '../components/TermsAccordion';
import PrivacyInfoBlock from '../components/PrivacyInfoBlock';
import { OPPORTUNITY_TYPES, INDUSTRIES, SKILL_TAGS } from '../constants';

const PostJobPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    opportunity_type: '',
    industry: '',
    organisation: '',
    city: '',
    country: 'India',
    salary: '',
    description: '',
    apply_link: '',
    allow_platform_application: false,
    direct_recruitment: false,
    closing_date: '',
    tags: [],
    poster_name: '',
    poster_email: '',
    poster_phone: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const toggleTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.opportunity_type || !formData.industry) {
      toast.error('Please select an opportunity type and industry.');
      return;
    }
    if (!formData.description || formData.description === '<p></p>') {
      toast.error('Please provide a description.');
      return;
    }
    if (!termsAgreed) {
      setShowTermsError(true);
      toast.error('Please accept the Terms & Conditions.');
      return;
    }
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/listings`, formData);
      toast.success('Opportunity submitted! It will be reviewed by our team.');
      setFormData({
        title: '',
        opportunity_type: '',
        industry: '',
        organisation: '',
        city: '',
        country: 'India',
        salary: '',
        description: '',
        apply_link: '',
        allow_platform_application: false,
        direct_recruitment: false,
        closing_date: '',
        tags: [],
        poster_name: '',
        poster_email: '',
        poster_phone: ''
      });
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />

      <SEO
        title="Post a Job"
        path="/post-job"
        description="Post a creative job on CultureCareers. Reach artists, designers, curators and cultural pros across India. Jobs, gigs, residencies, grants and open calls."
      />

      {/* Hero — matches /talent page structure */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" style={{ backgroundColor: '#0F2942' }}>
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight mb-6 text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Post a Job
          </h1>
          <div className="w-20 h-px mb-8" style={{ backgroundColor: 'rgba(212,130,10,0.6)' }} />
          <p className="text-base md:text-lg leading-relaxed font-light max-w-2xl mb-4" style={{ color: 'rgba(240, 235, 226, 0.65)' }}>
            Reach a curated network of artists, designers, curators, and creative professionals across India. From full-time roles to freelance gigs, speakers, and niche creative requirements.
          </p>
          <p className="text-sm max-w-2xl" style={{ color: 'rgba(240, 235, 226, 0.55)' }}>
            All listings are reviewed to maintain quality and relevance. Starting from &#8377;250.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-10 bg-white rounded-xl p-8 md:p-10 shadow-sm">
            {/* Poster Contact Info */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-4" style={{ color: '#D4820A' }}>Your Contact Details</p>
              <p className="text-xs mb-4" style={{ color: '#0F2942' }}>This information is only visible to us and will not be shown publicly.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Your Name *</label>
                  <Input
                    type="text"
                    value={formData.poster_name}
                    onChange={(e) => setFormData({ ...formData, poster_name: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="Full name"
                    data-testid="poster-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Your Email *</label>
                  <Input
                    type="email"
                    value={formData.poster_email}
                    onChange={(e) => setFormData({ ...formData, poster_email: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="you@example.com"
                    data-testid="poster-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Phone Number *</label>
                  <Input
                    type="tel"
                    value={formData.poster_phone}
                    onChange={(e) => setFormData({ ...formData, poster_phone: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="+91 98765 43210"
                    data-testid="poster-phone-input"
                  />
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* Title */}
            <div>
              <label className="text-sm font-medium mb-2 block">Title *</label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="rounded-none"
                placeholder="e.g. Keynote Speaker for Art Summit in Delhi"
                data-testid="title-input"
              />
            </div>

            {/* Opportunity Type & Industry */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Opportunity Type *</label>
                <Select value={formData.opportunity_type} onValueChange={(val) => setFormData({ ...formData, opportunity_type: val })}>
                  <SelectTrigger className="rounded-none" data-testid="opportunity-type-select">
                    <SelectValue placeholder="Select opportunity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPPORTUNITY_TYPES.map((ot) => (
                      <SelectItem key={ot} value={ot}>{ot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Industry / Domain *</label>
                <Select value={formData.industry} onValueChange={(val) => setFormData({ ...formData, industry: val })}>
                  <SelectTrigger className="rounded-none" data-testid="industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Organisation */}
            <div>
              <label className="text-sm font-medium mb-2 block">Organisation *</label>
              <Input
                type="text"
                value={formData.organisation}
                onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
                required
                className="rounded-none"
                placeholder="e.g. Kochi-Muziris Biennale Foundation"
                data-testid="organisation-input"
              />
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium mb-2 block">City *</label>
                <Input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="rounded-none"
                  placeholder="e.g. Mumbai"
                  data-testid="city-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Country *</label>
                <Input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                  className="rounded-none"
                  placeholder="e.g. India"
                  data-testid="country-input"
                />
              </div>
            </div>

            {/* Salary & Closing Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Compensation / Salary</label>
                <Input
                  type="text"
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  className="rounded-none"
                  placeholder="e.g. INR 5,00,000 - 8,00,000 per annum"
                  data-testid="salary-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Closing Date *</label>
                <Input
                  type="date"
                  value={formData.closing_date ? formData.closing_date.split('T')[0] : ''}
                  onChange={(e) => setFormData({ ...formData, closing_date: new Date(e.target.value + 'T23:59:59Z').toISOString() })}
                  required
                  className="rounded-none"
                  data-testid="closing-date-input"
                />
              </div>
            </div>

            {/* Rich Text Description */}
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <RichTextEditor
                content={formData.description}
                onChange={(html) => setFormData({ ...formData, description: html })}
                placeholder="Describe the opportunity, responsibilities, requirements, how to apply, etc."
              />
            </div>

            {/* Skill Tags */}
            <div>
              <label className="text-sm font-medium mb-2 block">Skill Tags (optional)</label>
              <p className="text-xs text-zinc-500 mb-3">Select relevant skills to help candidates find this opportunity.</p>
              <div className="flex flex-wrap gap-2" data-testid="skill-tags-container">
                {SKILL_TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                      formData.tags.includes(tag)
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-zinc-600 border-zinc-300 hover:border-black'
                    }`}
                    data-testid={`tag-${tag.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Application Link */}
            <div>
              <label className="text-sm font-medium mb-2 block">External Application Link</label>
              <Input
                type="url"
                value={formData.apply_link}
                onChange={(e) => setFormData({ ...formData, apply_link: e.target.value })}
                className="rounded-none"
                placeholder="https://..."
                data-testid="apply-link-input"
              />
            </div>

            {/* Platform Application Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="platform-app"
                checked={formData.allow_platform_application}
                onCheckedChange={(checked) => setFormData({ ...formData, allow_platform_application: checked })}
                data-testid="allow-platform-checkbox"
              />
              <label htmlFor="platform-app" className="text-sm text-zinc-700">
                Also allow applications through this platform
              </label>
            </div>

            {/* Submit */}
            <div className="pt-6" style={{ borderTop: '1px solid #F0EBE2' }}>
              <div className="mb-6">
                <TermsAccordion
                  variant="employer"
                  agreed={termsAgreed}
                  onAgreeChange={setTermsAgreed}
                  showError={showTermsError}
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-lg px-8 py-3 font-medium"
                style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}
                data-testid="submit-listing-button"
              >
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <div style={{ backgroundColor: '#F0EBE2' }}>
        <PrivacyInfoBlock
          testid="post-job-privacy-info"
          heading="How your submission is handled"
          points={[
            'All submissions are reviewed before being published to maintain the quality and relevance of opportunities on the platform.',
            'Information provided is used to create and share your listing and to connect you with suitable candidates where relevant.',
            'We do not share your details beyond what is necessary for the opportunity or its visibility.',
            'Our goal is to ensure that each listing reaches the right audience in a thoughtful and structured way.'
          ]}
        />
      </div>

      <Footer />
    </div>
  );
};

export default PostJobPage;
